import {SLIDER_CURVE_EXPONENT, SLIDER_POSITION_MAX, STACK_FACTORS} from './constants'
import type {CurrencyCode, Savings, SavingsInput, SliderConfig, Volumes} from './types'

const MINUTES_PER_HOUR = 60

export const clampNumber = (value: number, min: number, max: number): number =>
  Number.isNaN(value) ? min : Math.min(max, Math.max(min, value))

export const valueFromPosition = (position: number, {max, snap}: SliderConfig): number => {
  const normalizedPosition = clampNumber(position, 0, SLIDER_POSITION_MAX) / SLIDER_POSITION_MAX

  return Math.round((normalizedPosition ** SLIDER_CURVE_EXPONENT * max) / snap) * snap
}

export const positionFromValue = (value: number, {max}: SliderConfig): number =>
  Math.round((clampNumber(value, 0, max) / max) ** (1 / SLIDER_CURVE_EXPONENT) * SLIDER_POSITION_MAX)

export const stepValue = (value: number, direction: 1 | -1, {max, snap}: SliderConfig, steps = 1): number =>
  clampNumber(value + direction * snap * steps, 0, max)

const hoursFor = (volume: number, minutesPerTransaction: number, stackFactor: number): number =>
  (volume * minutesPerTransaction * stackFactor) / MINUTES_PER_HOUR

export const calculateSavings = ({volumes, minutesPerTransaction, hourlyRate, stack}: SavingsInput): Savings => {
  const stackFactor = STACK_FACTORS[stack]

  const hours: Volumes = {
    cards: hoursFor(volumes.cards, minutesPerTransaction, stackFactor),
    invoices: hoursFor(volumes.invoices, minutesPerTransaction, stackFactor),
    reimbursements: hoursFor(volumes.reimbursements, minutesPerTransaction, stackFactor),
  }

  const totalHours = hours.cards + hours.invoices + hours.reimbursements

  return {hours, totalHours, money: totalHours * hourlyRate}
}

export const barWidths = (hours: Volumes): Volumes => {
  const largest = Math.max(hours.cards, hours.invoices, hours.reimbursements)
  const widthOf = (value: number): number => (largest === 0 ? 0 : (value * 100) / largest)

  return {
    cards: widthOf(hours.cards),
    invoices: widthOf(hours.invoices),
    reimbursements: widthOf(hours.reimbursements),
  }
}

export const formatDays = (days: number): string => (days > 0 && days < 1 ? days.toFixed(1) : String(Math.round(days)))

export const formatNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {maximumFractionDigits: 0}).format(value)

export const getCurrencySymbol = (locale: string, currency: CurrencyCode): string =>
  new Intl.NumberFormat(locale, {style: 'currency', currency}).formatToParts(0).find((part) => part.type === 'currency')
    ?.value ?? currency

export const COMPACT_THRESHOLD = 100_000

export const MOBILE_COMPACT_THRESHOLD = 10_000
const THOUSAND = 1_000
const MILLION = 1_000_000
const NO_BREAK_SPACE = '\u00a0'

const COMPACT_SUFFIXES: Record<string, {thousand: string; million: string}> = {
  de: {thousand: `${NO_BREAK_SPACE}Tsd.`, million: `${NO_BREAK_SPACE}Mio.`},
  nl: {thousand: 'K', million: `${NO_BREAK_SPACE}mln.`},
  en: {thousand: 'K', million: 'M'},
}

// Hand-rolled because Intl compact notation leaves German thousands unabbreviated (123.410 €)
export const formatCompactCurrency = (
  value: number,
  locale: string,
  currency: CurrencyCode,
  threshold = COMPACT_THRESHOLD,
): string => {
  if (Math.abs(value) < threshold) {
    return formatCurrency(value, locale, currency)
  }

  const isMillions = Math.round(Math.abs(value) / THOUSAND) >= THOUSAND
  const suffixes = COMPACT_SUFFIXES[locale.split('-')[0]] ?? COMPACT_SUFFIXES.en

  const formattedAmount = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: isMillions ? 1 : 0,
  }).format(value / (isMillions ? MILLION : THOUSAND))

  const lastDigitIndex = formattedAmount.search(/\d(?!.*\d)/) + 1

  return `${formattedAmount.slice(0, lastDigitIndex)}${isMillions ? suffixes.million : suffixes.thousand}${formattedAmount.slice(lastDigitIndex)}`
}

export const formatCurrency = (value: number, locale: string, currency: CurrencyCode): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
